import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Sparkles, Search, Loader2, ArrowRight } from "lucide-react";
import {
  getDemoMode,
  listSmartImproveDemoProfiles,
} from "../lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const complexityStyle = (lvl) => {
  switch ((lvl || "").toLowerCase()) {
    case "low":
      return "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]";
    case "medium":
      return "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]";
    case "high":
      return "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]";
    case "very high":
      return "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]";
    default:
      return "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]";
  }
};

/**
 * SmartImproveLoadDemoButton
 * --------------------------
 * Strict role gate: visible only to admin/demo users.
 *
 * UX (Feb 2026 update): selecting a profile does NOT create an
 * engagement. It fetches the full profile data and calls onApplied so
 * the parent (SmartImprove intake page) can prefill its local state and
 * let the user review/edit through Steps 1/2/3 before any DB write
 * happens. The engagement + sites + findings are only created on the
 * intake page's final submit.
 *
 * Props:
 *   - onApplied: (profile) => void   // called with the full profile doc
 *   - variant: "compact" | "full"    // visual density for the trigger
 */
export default function SmartImproveLoadDemoButton({
  onApplied,
  variant = "compact",
}) {
  const [enabled, setEnabled] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loadingId, setLoadingId] = useState(null);

  // Strict role gate — admin/demo only.
  useEffect(() => {
    let cancelled = false;
    getDemoMode()
      .then((d) => {
        if (cancelled) return;
        const role = (d?.role || "").toLowerCase();
        setEnabled(role === "admin" || role === "demo");
      })
      .catch(() => !cancelled && setEnabled(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Lazily fetch the profile catalog when the modal opens.
  useEffect(() => {
    if (!open || profiles.length || !enabled) return;
    listSmartImproveDemoProfiles()
      .then((rows) => setProfiles(Array.isArray(rows) ? rows : []))
      .catch(() => setProfiles([]));
  }, [open, profiles.length, enabled]);

  const filtered = useMemo(() => {
    if (!search.trim()) return profiles;
    const q = search.toLowerCase();
    return profiles.filter((p) =>
      [
        p.profile_name,
        p.profile_description,
        p.industry,
        p.complexity_label,
        p.strategic_objective,
      ]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [profiles, search]);

  // Fetch the full profile (with sites + findings) — used to prefill the
  // intake page state. NO backend write; NO navigation.
  const fetchFullProfile = async (profileId) => {
    const res = await axios.get(
      `${API}/smartimprove/demo-profiles/${profileId}`,
      { withCredentials: true },
    );
    return res.data;
  };

  const handleSelect = async (p) => {
    if (loadingId) return;
    setLoadingId(p.id);
    try {
      const full = await fetchFullProfile(p.id);
      setOpen(false);
      toast.success(`Demo profile loaded — review and edit before submitting`);
      if (onApplied) onApplied(full);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to load demo profile");
    } finally {
      setLoadingId(null);
    }
  };

  if (!enabled) return null;

  const buttonCls =
    variant === "compact"
      ? "inline-flex items-center gap-1.5 rounded-sm border border-[#025979]/25 bg-white text-[#025979] hover:bg-[#E0F2FE] px-3 py-1.5 text-xs font-medium transition-colors"
      : "inline-flex items-center gap-2 rounded-sm border border-[#025979] bg-white text-[#025979] hover:bg-[#E0F2FE] px-4 py-2 text-sm font-medium transition-colors";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={buttonCls}
        data-testid="si-load-demo-profile-btn"
      >
        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />
        Load Demo Profile
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0"
          data-testid="si-demo-profile-selector"
        >
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle
              className="text-base font-semibold text-[#111827]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Load Demo Profile
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Select a preseeded SmartImprove scenario to prefill the intake
              form. You can review and edit every step before submitting —
              nothing is saved until you finalize the engagement.
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, industry, or complexity…"
                className="pl-8 h-9 text-xs rounded-sm"
                data-testid="si-demo-profile-search"
              />
            </div>
          </div>

          <div
            className="px-6 pb-6 overflow-y-auto space-y-2"
            data-testid="si-demo-profile-list"
          >
            {filtered.length === 0 ? (
              <div className="py-8 text-center text-xs text-[#9CA3AF]">
                {profiles.length
                  ? "No profiles match your search."
                  : "Loading profiles…"}
              </div>
            ) : (
              filtered.map((p) => {
                const busy = loadingId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleSelect(p)}
                    disabled={!!loadingId}
                    className="w-full text-left rounded-sm border border-[#E5E7EB] bg-white p-3.5 hover:border-[#025979] hover:shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`si-demo-profile-option-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-[#111827] truncate">
                            {p.profile_name}
                          </h4>
                          {p.default && (
                            <span className="px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] text-[10px] font-medium">
                              Default
                            </span>
                          )}
                        </div>
                        {p.profile_description && (
                          <p className="text-[11px] text-[#6B7280] mt-1 line-clamp-2">
                            {p.profile_description}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {p.industry && (
                            <span className="px-2 py-0.5 rounded-full border text-[10px] bg-[#E6F4FA] text-[#025979] border-[#B5DEF0]">
                              {p.industry}
                            </span>
                          )}
                          {p.complexity_label && (
                            <span
                              className={`px-2 py-0.5 rounded-full border text-[10px] ${complexityStyle(p.complexity_label)}`}
                            >
                              {p.complexity_label} Complexity
                            </span>
                          )}
                          {Array.isArray(p.warehouse_landscape) &&
                          p.warehouse_landscape.length ? (
                            <span className="px-2 py-0.5 rounded-full border text-[10px] bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] truncate max-w-[260px]">
                              {p.warehouse_landscape.slice(0, 2).join(" · ")}
                              {p.warehouse_landscape.length > 2 ? " · …" : ""}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="shrink-0 pt-1 text-[#025979]">
                        {busy ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ArrowRight className="w-4 h-4 opacity-60" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
